// Lifted from ~/Desktop/Reputifly AI Local/app/src/components/AttachmentChip.tsx
// Adaptation: type import path changed (../types -> ../lib/types).

import type { AttachItem } from "../lib/types";

interface Props {
  item: AttachItem;
  onRemove: (id: string) => void;
  onPreview: (item: AttachItem) => void;
  /** Mouse entered the chip body -- App expands a center-screen preview. */
  onHoverEnter?: (item: AttachItem) => void;
  /** Mouse left the chip body -- App contracts the preview away. */
  onHoverLeave?: () => void;
  /** Pencil on a ready image chip: opens the annotate view. */
  onEdit?: (item: AttachItem, button: HTMLButtonElement) => void;
}

/**
 * ChatGPT-style attachment chip:
 *   - Compact 64x64 rounded thumbnail
 *   - Floating X in the top-right corner for instant removal
 *   - Click body opens the persistent preview lightbox
 *   - HOVER body triggers a center-of-screen expand preview that
 *     contracts back when the mouse leaves (handled in App.tsx)
 */
export default function AttachmentChip({
  item,
  onRemove,
  onPreview,
  onHoverEnter,
  onHoverLeave,
  onEdit,
}: Props) {
  const isCompressing = item.status === "compressing";
  const isError = item.status === "error";
  const isImage = item.kind === "image" || !!item.blobUrl;
  const canEdit = !!onEdit && item.status === "ready" && item.kind === "image" && !!item.blobUrl;
  return (
    <div className={`attachChip ${isCompressing ? "is-compressing" : ""} ${isError ? "is-error" : ""}`}>
      <button
        type="button"
        className="attachChip__body"
        onClick={() => !isCompressing && onPreview(item)}
        onMouseEnter={() => {
          if (!isCompressing && onHoverEnter) onHoverEnter(item);
        }}
        onMouseLeave={() => {
          if (onHoverLeave) onHoverLeave();
        }}
        aria-label={
          isCompressing
            ? `Compressing ${item.filename}`
            : `Preview ${item.filename}`
        }
        disabled={isCompressing}
        title={item.filename}
      >
        {isImage && item.blobUrl ? (
          <img src={item.blobUrl} alt="" draggable={false} />
        ) : (
          <div className="attachChip__placeholder">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="22" height="22" aria-hidden="true">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          </div>
        )}
        {isCompressing && (
          <div className="attachChip__overlay">
            <div className="attachChip__spinner" />
          </div>
        )}
      </button>
      {canEdit && (
        <button
          type="button"
          className="attachChip__edit"
          onClick={(e) => {
            e.stopPropagation();
            onEdit!(item, e.currentTarget);
          }}
          aria-label={`Draw on ${item.filename}`}
          title="Draw on image"
        >
          {/* Lucide pencil */}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
            <path d="m15 5 4 4" />
          </svg>
        </button>
      )}
      <button
        type="button"
        className="attachChip__remove"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(item.id);
        }}
        aria-label="Remove attachment"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="10"
          height="10"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
}
